<template>
  <div v-if="activeQuestions.length">
    <div v-if="title" class="text-subtitle-1 font-weight-bold mb-2">
      {{ title }}
      <small v-if="required" class="text-red">*Required</small>
    </div>
    <div v-if="description" class="text-body-2 mb-3" v-html="description" />

    <template v-for="(ques, i) in activeQuestions" :key="ques.questionID">
      <div v-if="shouldShowSection(ques, i)" class="mb-2">
        <h2 class="text-h6 font-weight-bold">{{ ques.section }}</h2>
        <v-divider class="my-2" />
      </div>
      <div class="mb-4">
        <h3 class="mb-2">
          {{ fillPlaceholders(ques.question) }}
          <small class="text-red font-weight-bold" v-if="ques.isRequired">*Required</small>
          <br />
          <small v-if="ques.description" v-html="ques.description" />
        </h3>

        <v-text-field
          v-if="ques.questionType == 'Textbox'"
          density="compact"
          hide-details="auto"
          @change="saveAnswer(ques, $event.target.value)"
        />

        <v-text-field
          v-if="ques.questionType == 'Number'"
          type="number"
          density="compact"
          hide-details="auto"
          @change="saveAnswer(ques, $event.target.value)"
        />

        <v-autocomplete
          v-if="ques.questionType && ques.questionType.includes('Select')"
          v-model="dropDownAnswers[ques.questionID]"
          :items="getOptions(ques)"
          :multiple="ques.questionType == 'MultiSelect'"
          density="compact"
          hide-details="auto"
        />

        <template v-if="ques.questionType == 'Checkbox'">
          <v-checkbox
            v-for="opt in getOptions(ques)"
            :key="opt"
            :label="opt"
            :value="opt"
            v-model="dropDownAnswers[ques.questionID]"
            density="compact"
            hide-details="auto"
          />
        </template>

        <VueDatePicker
          v-if="ques.questionType == 'DatePicker'"
          v-model="dropDownAnswers[ques.questionID]"
          :enable-time-picker="false"
          auto-apply
          format="dd/MMM/yyyy"
          :text-input="{ format: 'dd/MMM/yyyy' }"
          model-type="dd-MMM-yyyy"
          placeholder="dd/MMM/yyyy"
        />

        <v-radio-group
          v-if="ques.questionType == 'RadioGroup'"
          density="compact"
          hide-details="auto"
          @change="saveAnswer(ques, $event.target.value)"
        >
          <v-radio v-for="opt in getOptions(ques)" :key="opt" :label="opt" :value="opt" />
        </v-radio-group>

        <v-file-input
          v-if="ques.questionType == 'FileUpload' && allowFiles"
          label="Select File"
          density="compact"
          variant="outlined"
          multiple
          :accept="getFileAcceptAttr(ques.options) || undefined"
          :hint="getFileAcceptHint(ques.options, ques.maxFileSizeMb) || undefined"
          :persistent-hint="Boolean(getFileAcceptHint(ques.options, ques.maxFileSizeMb))"
          @change="onFileChange(ques, $event.target.files)"
        />
        <v-alert
          v-else-if="ques.questionType == 'FileUpload' && !allowFiles"
          type="warning"
          variant="tonal"
          density="compact"
        >
          File upload questions must be filled by the member on their own form.
        </v-alert>
      </div>
      <v-divider v-if="i < questions.length - 1" class="mb-3" />
    </template>
  </div>
</template>

<script setup>
import { reactive, ref, watch, inject, markRaw, computed } from "vue";
import { useAuthStore } from "@/setup/stores/authStore";
import {
  getFileAcceptAttr,
  getFileAcceptHint,
  validateFilesAgainstAccept,
} from "@/services/surveyService";

const props = defineProps({
  questions: { type: Array, default: () => [] },
  title: { type: String, default: "" },
  description: { type: String, default: "" },
  required: { type: Boolean, default: false },
  allowFiles: { type: Boolean, default: true },
});

const snackbar = inject("snackBar");
const authStore = useAuthStore();

const answers = markRaw([]);
const dropDownAnswers = reactive({});
const files = ref([]);

const activeQuestions = computed(() =>
  (props.questions || []).filter((q) => q.isActive !== false)
);

function resetState() {
  answers.splice(0, answers.length);
  Object.keys(dropDownAnswers).forEach((k) => delete dropDownAnswers[k]);
  files.value = [];

  activeQuestions.value.forEach((ques) => {
    answers.push({ questionID: ques.questionID, answer: "" });
    if (ques.questionType == "MultiSelect" || ques.questionType == "Checkbox")
      dropDownAnswers[ques.questionID] = [];
    if (ques.questionType == "FileUpload")
      files.value.push({ questionID: ques.questionID, files: [] });
  });
}

watch(() => props.questions, resetState, { immediate: true, deep: true });

function fillPlaceholders(question) {
  let text = question || "";
  const vals = {
    FullName: authStore.user.username,
    Team: authStore.user.organization,
    Section: authStore.user.section,
    Jamaat: authStore.user.jamaat,
    Rank: authStore.user.rank,
    ItsId: authStore.user.itsId,
  };
  Object.keys(vals).forEach((key) => {
    text = text.replace(`[${key}]`, vals[key] ?? "");
  });
  return text;
}

function shouldShowSection(ques, index) {
  if (!ques.section) return false;
  if (index === 0) return true;
  return ques.section !== activeQuestions.value[index - 1].section;
}

function getOptions(ques) {
  if (!ques.options) return [];
  return ques.options.split(",");
}

function saveFile(ques, uploads) {
  const fl = files.value.find((x) => x.questionID == ques.questionID);
  if (fl) fl.files = uploads;
}

function onFileChange(ques, uploads) {
  const error = validateFilesAgainstAccept(uploads, ques.options, ques.maxFileSizeMb);
  if (error) {
    snackbar.error(error);
    saveFile(ques, []);
    return;
  }
  saveFile(ques, uploads);
}

function saveAnswer(ques, value) {
  const ans = answers.find((x) => x.questionID == ques.questionID);
  if (ans) ans.answer = value;
}

function collectAnswers() {
  let hasError = false;

  activeQuestions.value.forEach((ques) => {
    const ans = answers.find((x) => x.questionID == ques.questionID);
    const fl = files.value.find((x) => x.questionID == ques.questionID);
    const dropdownans = dropDownAnswers[ques.questionID];

    if (dropdownans !== undefined && dropdownans !== null && dropdownans !== "")
      ans.answer = Array.isArray(dropdownans) ? dropdownans.toString() : String(dropdownans);

    if (ques.isRequired && ques.questionType != "FileUpload" && !ans?.answer) {
      hasError = true;
      snackbar.error(`Answer for question: ${ques.question} is required.`);
    }

    if (ques.isRequired && ques.questionType == "FileUpload") {
      if (!props.allowFiles) {
        hasError = true;
        snackbar.error(`File upload for question: ${ques.question} cannot be completed here.`);
      } else if (!fl || fl.files.length === 0) {
        hasError = true;
        snackbar.error(`File upload for question: ${ques.question} is required.`);
      }
    }

    if (ques.questionType == "FileUpload" && fl?.files?.length) {
      const typeError = validateFilesAgainstAccept(fl.files, ques.options, ques.maxFileSizeMb);
      if (typeError) {
        hasError = true;
        snackbar.error(typeError);
      }
    }
  });

  if (hasError) return null;

  return {
    answers: answers.map((a) => ({ questionID: a.questionID, answer: a.answer || "" })),
    files: files.value.filter((f) => f.files && f.files.length > 0),
  };
}

defineExpose({ collectAnswers, resetState });
</script>
